import { DistancePipe } from 'app/modules/dashboard/pipes/distance.pipe';
import {
  BrowseFilter,
  FilterNamesEnum,
  getFiltersCopy,
} from '../models/filter';
import {
  FilterOption,
  getDistanceFilterOptions,
  getSearchModeFilterOptions,
  getSlopeFilterOptions,
  getSpeedFilterOptions,
} from '../models/filter-options';
import { Unit } from '@kinomap-dev/ng-ui-lib';

// APIs

export const getDurationParams = (filters: BrowseFilter[]): string[] => {
  const durationOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Duration
  ).selectedOptions;

  if (!durationOptions.length) {
    return ['', ''];
  }

  let [min, max] = getMinAndMax(durationOptions);

  min = (+min * 60).toString();
  if (!!max) {
    max = (+max * 60).toString();
  }

  return [min, max];
};

export const getDistanceParams = (filters: BrowseFilter[]): string[] => {
  const distanceOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Distance
  ).selectedOptions;
  if (!distanceOptions.length) {
    return ['', ''];
  }

  const minAndMax: string[] = getMinAndMax(distanceOptions);

  if (+minAndMax[1] === getMaxDefaultOptionValue(FilterNamesEnum.Distance)) {
    minAndMax[1] = '';
  }

  return minAndMax;
};

export const getSlopeParams = (filters: BrowseFilter[]): string[] => {
  const slopeOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Slope
  ).selectedOptions;
  if (!slopeOptions.length) {
    return ['', ''];
  }

  const minAndMax: string[] = getMinAndMax(slopeOptions);

  if (+minAndMax[1] === getMaxDefaultOptionValue(FilterNamesEnum.Slope)) {
    minAndMax[1] = '';
  }

  return minAndMax;
};

export const getDifficultiesParam = (filters: BrowseFilter[]): string[] => {
  return filters
    .find((filter: BrowseFilter) => filter.name === FilterNamesEnum.Difficulty)
    .selectedOptions.map((option: FilterOption) => option.value);
};

export const getQualitiesParam = (filters: BrowseFilter[]): string[] => {
  return filters
    .find((f: BrowseFilter) => f.name === FilterNamesEnum.Quality)
    .selectedOptions.map((option: FilterOption) => option.value);
};

export const getProgressParam = (
  filters: BrowseFilter[]
): {
  excludeComplete: '1' | '';
  excludeStarted: '1' | '';
} => {
  const progressOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Progress
  ).selectedOptions;

  const excludeComplete: '1' | '' = progressOptions.find(
    (o) => o.value === 'excludeComplete'
  )
    ? '1'
    : '';
  const excludeStarted: '1' | '' = progressOptions.find(
    (o) => o.value === 'excludeStarted'
  )
    ? '1'
    : '';

  return {
    excludeComplete,
    excludeStarted,
  };
};

export const getSortDirectionParam = (filters: BrowseFilter[]): string => {
  const sortDirectionFilter: BrowseFilter = filters.find(
    (f: BrowseFilter) => f.name === FilterNamesEnum.SortDirection
  );
  const selected: string = sortDirectionFilter?.selectedOptions?.[0]?.value;
  return selected === 'desc' ? 'desc' : 'asc';
};

export const getSoundParam = (filters: BrowseFilter[]): string => {
  const soundOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Sound
  ).selectedOptions;

  if (!soundOptions.length) {
    return '';
  }

  return soundOptions[0].value;
};

export const getSpeedParams = (filters: BrowseFilter[]): string[] => {
  const speedOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Speed
  ).selectedOptions;
  if (!speedOptions.length) {
    return ['', ''];
  }

  const minAndMaxInKmH: string[] = getMinAndMax(speedOptions);

  if (+minAndMaxInKmH[1] === getMaxDefaultOptionValue(FilterNamesEnum.Speed)) {
    minAndMaxInKmH[1] = '';
  }

  const minAndMaxInMS: string[] = minAndMaxInKmH.map((value) => {
    if (value !== '') {
      return ((+value * 5) / 18).toString();
    }
    return value;
  });

  return minAndMaxInMS;
};

export const getPowerParams = (filters: BrowseFilter[]): string[] => {
  const powerOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.Power
  ).selectedOptions;
  if (!powerOptions.length) {
    return ['', ''];
  }

  return getMinAndMax(powerOptions);
};

export const getTagParam = (filters: BrowseFilter[]): number[] => {
  return filters
    .find((filter: BrowseFilter) => filter.name === FilterNamesEnum.Tag)
    .selectedOptions.map((option: FilterOption) => +option.value);
};

export const getRegionParam = (filters: BrowseFilter[]): string[] => {
  const regionFilter: BrowseFilter = filters.find(
    (filter: BrowseFilter) => filter.name === FilterNamesEnum.Region
  );
  if (!regionFilter) return [];
  return regionFilter.selectedOptions.flatMap((option: FilterOption) =>
    option.value.split(',')
  );
};

export const getLangGroupParam = (filters: BrowseFilter[]): string[] => {
  return filters
    .find((filter: BrowseFilter) => filter.name === FilterNamesEnum.Language)
    .selectedOptions.map((option: FilterOption) => option.value);
};

export const getMachineTypeParam = (filters: BrowseFilter[]): string[] => {
  return filters
    .find((filter: BrowseFilter) => filter.name === FilterNamesEnum.Equipment)
    .selectedOptions.map((option: FilterOption) => option.value);
};

export const getModesParam = (filters: BrowseFilter[]): string[] => {
  return filters
    .find((filter: BrowseFilter) => filter.name === FilterNamesEnum.WorkoutType)
    .selectedOptions.map((option: FilterOption) => option.value);
};

export const getInAppParam = (filters: BrowseFilter[]): string => {
  const inAppOptions: FilterOption[] = filters.find(
    (f) => f.name === FilterNamesEnum.InApp
  ).selectedOptions;
  if (inAppOptions.length === 1) {
    return inAppOptions[0].value;
  }
  return '0';
};

export const getClimbCategoriesParam = (filters: BrowseFilter[]): number[] => {
  return filters
    .find(
      (filter: BrowseFilter) => filter.name === FilterNamesEnum.ClimbCategory
    )
    .selectedOptions.map((option: FilterOption) => +option.value);
};

// export const getVehiclesParam = (filters: BrowseFilter[]): string[] => {
//   return filters
//     .find((filter: BrowseFilter) => filter.name === FilterNamesEnum.Vehicle)
//     .selectedOptions.map((option: FilterOption) => option.value);
// };

const getMinAndMax = (options: FilterOption[]): string[] => {
  let min = Math.min(
    ...options.map((option) => {
      return +option.value.split('-')[0];
    })
  ).toString();
  let max = Math.max(
    ...options.map((option) => {
      return +option.value.split('-')[1];
    })
  ).toString();
  if (
    options
      .map((option) => {
        return option.value.split('-')[1];
      })
      .some((v) => v === '0')
  ) {
    max = '';
  }

  return [min, max];
};

const getOptionValuesConcatenatedWithPipes = (
  options: FilterOption[]
): string => {
  const values: string[] = options.map((o) => o.value);
  return values.join('|');
};

// Translation

export const getTranslationParameters = (
  option: FilterOption,
  unit: Unit = 'metric'
): { minValue?: string; maxValue?: string; unit: string } | null => {
  if (!option.value.includes('-')) {
    return null;
  }

  const key: string = option.label.match(/filters\.([^.]+)\.options/)?.[1];
  let [min, max] = option.value.split('-');
  let valueUnit: string = '';
  switch (key) {
    case 'distance':
    case 'slope':
      const distancePipe: DistancePipe = new DistancePipe();
      min = distancePipe.transform(+min * 1000, 'value', unit);
      max = distancePipe.transform(+max * 1000, 'value', unit);
      valueUnit = distancePipe.transform(
        (+min ? +min : +max) * 1000,
        'unit',
        unit
      );
      break;
  }

  if (+min === 0) {
    return { maxValue: max, unit: valueUnit };
  }

  if (+max === 0) {
    return { minValue: min, unit: valueUnit };
  }

  return { minValue: min, maxValue: max, unit: valueUnit };
};

//

export const areFiltersDifferent = (
  lastFilters: BrowseFilter[],
  newFilters: BrowseFilter[]
): boolean => {
  if (!lastFilters.length) {
    return true;
  }

  const oldValues = {};
  lastFilters.forEach((filter) => {
    oldValues[filter.name] = filter.selectedOptions.map(
      (option) => option.value
    );
  });

  const newValues = {};
  newFilters.forEach((filter) => {
    newValues[filter.name] = filter.selectedOptions.map(
      (option) => option.value
    );
  });

  return Object.keys(newValues).some((key) => {
    return (
      oldValues[key].some((value) => {
        return !newValues[key].includes(value);
      }) ||
      newValues[key].some((value) => {
        return !oldValues[key].includes(value);
      })
    );
  });
};

export const haveOptionsChanged = (
  filter: BrowseFilter,
  selectedOptions: FilterOption[]
): boolean => {
  return (
    selectedOptions.some((option) => {
      return !filter.selectedOptions.map((o) => o.value).includes(option.value);
    }) ||
    filter.selectedOptions.some((option) => {
      return !selectedOptions.map((o) => o.value).includes(option.value);
    })
  );
};

export const isOptionSelected = (
  option: FilterOption,
  selectedOptions: FilterOption[]
): boolean => {
  if (isCustomFilterOption(option)) {
    return selectedOptions.some((selectedOption: FilterOption) => {
      return isCustomFilterOption(selectedOption);
    });
  }

  return selectedOptions.some((selectedOption: FilterOption) => {
    return selectedOption.value === option.value;
  });
};

export const isOptionEnabled = (
  option: FilterOption,
  selectedOptions: FilterOption[],
  filter: BrowseFilter,
  filterOptions: FilterOption[]
): boolean => {
  if (isCustomFilterOption(option)) {
    return true;
  }

  const isChecked: boolean = isOptionChecked(option, selectedOptions);
  const filtersThatAreNotIntervals: FilterNamesEnum[] = [
    FilterNamesEnum.Tag,
    FilterNamesEnum.Progress,
    FilterNamesEnum.Language,
    FilterNamesEnum.Equipment,
    FilterNamesEnum.WorkoutType,
    FilterNamesEnum.ClimbCategory,
    FilterNamesEnum.Region,
    FilterNamesEnum.Distance,
    FilterNamesEnum.Duration,
    FilterNamesEnum.Difficulty,
    FilterNamesEnum.Quality,
    FilterNamesEnum.Power,
    // FilterNamesEnum.Vehicle,
  ];

  if (
    filterOptions.length === 1 ||
    !selectedOptions.length ||
    filtersThatAreNotIntervals.includes(filter.name)
  ) {
    return true;
  }

  const surroundingOptions = getSurroundingOptions(option, filterOptions);

  if (isChecked) {
    return (
      surroundingOptions.length < 2 ||
      surroundingOptions.some((o) => !isOptionChecked(o, selectedOptions))
    );
  }

  return surroundingOptions.some((o) => {
    return isOptionChecked(o, selectedOptions);
  });
};

const isOptionChecked = (
  option: FilterOption,
  selectedOptions: FilterOption[]
): boolean => {
  if (isCustomFilterOption(option)) {
    return selectedOptions.some((selectedOption: FilterOption) => {
      return isCustomFilterOption(selectedOption);
    });
  }

  return selectedOptions.some((o) => o.value === option.value);
};

const getSurroundingOptions = (
  option: FilterOption,
  filterOptions: FilterOption[]
): FilterOption[] => {
  if (filterOptions.length < 2) {
    return [];
  }

  const index: number = filterOptions.indexOf(option);
  if (index === 0) {
    return [filterOptions[1]];
  }
  if (index === filterOptions.length - 1) {
    return [filterOptions[index - 1]];
  }
  return [filterOptions[index - 1], filterOptions[index + 1]];
};

export const generateFilterOptionId = (filterOption: FilterOption): string => {
  return `${filterOption.label}-${filterOption.value}`;
};

export const getFilterNamesWithSlider = (): FilterNamesEnum[] => {
  return [FilterNamesEnum.Slope, FilterNamesEnum.Speed];
};

export const getFiltersWithCustomSliderOption = (): FilterNamesEnum[] => {
  return [FilterNamesEnum.Distance];
};

export const filterHasCustomSliderOption = (
  filterName: FilterNamesEnum
): boolean => {
  return getFiltersWithCustomSliderOption().includes(filterName);
};

export const getCustomSliderDefaultOption = (
  filterName: FilterNamesEnum
): FilterOption | undefined => {
  if (!filterHasCustomSliderOption(filterName)) {
    return undefined;
  }

  switch (filterName) {
    case FilterNamesEnum.Distance:
      return getDistanceFilterOptions().find((option) => option.isCustom);
  }
};

export const isCustomFilterOption = (option: FilterOption): boolean => {
  return !!option.isCustom;
};

export const getSelectedCustomOption = (
  selectedOptions: FilterOption[]
): FilterOption | undefined => {
  return selectedOptions.find((option) => option.isCustom);
};

export const isCustomSelectionForFilter = (
  option: FilterOption,
  filterName: FilterNamesEnum,
  filterOptions: FilterOption[]
): boolean => {
  if (!filterHasCustomSliderOption(filterName)) {
    return false;
  }

  if (isCustomFilterOption(option)) {
    return true;
  }

  const predefinedValues: string[] = filterOptions
    .filter((filterOption: FilterOption) => {
      return !isCustomFilterOption(filterOption);
    })
    .map((filterOption: FilterOption) => filterOption.value);

  return !predefinedValues.includes(option.value);
};

export const isFilterActive = (filter: BrowseFilter): boolean => {
  if (!getFilterNamesWithSlider().includes(filter.name)) {
    return !!filter.selectedOptions.length;
  }

  if (!filter.selectedOptions.length) {
    return false;
  }

  const defaultOptionValue: string = getDefaultSelectedOptions(filter.name)[0]
    ?.value;
  return filter.selectedOptions[0].value !== defaultOptionValue;
};

export const getDefaultSelectedOptions = (
  filterName: FilterNamesEnum
): FilterOption[] => {
  if (
    !getFilterNamesWithSlider().includes(filterName) &&
    !getFiltersCopy().find((f) => f.name === filterName).isRadio
  ) {
    return [];
  }

  switch (filterName) {
    case FilterNamesEnum.Distance:
      return getDistanceFilterOptions();
    case FilterNamesEnum.SearchMode:
      return [getSearchModeFilterOptions()[0]];
    case FilterNamesEnum.Slope:
      return getSlopeFilterOptions();
    case FilterNamesEnum.Speed:
      return getSpeedFilterOptions();
  }
};

const getMaxDefaultOptionValue = (filterName: FilterNamesEnum): number => {
  const defaultOptions: FilterOption[] =
    getDefaultSelectedOptions(filterName) ?? [];

  if (defaultOptions.length) {
    return +defaultOptions[0].value.split('-')[1];
  }

  switch (filterName) {
    case FilterNamesEnum.Distance:
      return +(
        getCustomSliderDefaultOption(filterName)?.value.split('-')[1] ?? 0
      );
    default:
      return 0;
  }
};

export const getFilterUnity = (
  filterName: FilterNamesEnum,
  unit: Unit = 'metric'
): string => {
  const distancePipe: DistancePipe = new DistancePipe();

  switch (filterName) {
    case FilterNamesEnum.Duration:
      return 'min';
    case FilterNamesEnum.Distance:
    case FilterNamesEnum.Slope:
      return distancePipe.transform(0, 'unit', unit);
    case FilterNamesEnum.Speed:
      return 'km/h';
    case FilterNamesEnum.Power:
      return 'watts';
    default:
      return '';
  }
};

export const canFilterOptionsAllBeSelected = (
  filterName: FilterNamesEnum
): boolean => {
  const filtersWhoseOptionsCanAllBeSelected: FilterNamesEnum[] = [
    FilterNamesEnum.Tag,
    FilterNamesEnum.Progress,
    // FilterNamesEnum.Vehicle,
  ];
  return filtersWhoseOptionsCanAllBeSelected.includes(filterName);
};
